/**
 * useAppEnv Hook
 *
 * 兼容 SSR/CSR 环境检测
 * - 服务端环境: 使用 Context 中的 serverUA
 * - 客户端环境: 自动从 window.navigator.userAgent 获取
 *
 * 使用方式:
 * ```tsx
 * import { useAppEnv } from '@/hooks/useAppEnv';
 *
 * function MyComponent() {
 *   const appEnv = useAppEnv();
 *   // appEnv.isMT, appEnv.isIos, etc.
 * }
 * ```
 */

import { getAppEnv, type ParsedBrowserEnv } from '@/lib/getBrowserEnv';
import { useServerUA } from '@/context/PageStateContext';

/**
 * 获取当前环境信息
 * - 服务端渲染时: 使用服务端传入的 UA（从 Context 获取）
 * - 客户端渲染时: 自动从 window.navigator.userAgent 获取
 *
 * @returns 解析后的环境信息对象
 */
export function useAppEnv(): ParsedBrowserEnv {
  const serverUA = useServerUA();
  // 如果有 serverUA，说明是服务端渲染，使用传入的 UA
  // 否则是客户端渲染，getAppEnv() 会自动从 window 获取
  return getAppEnv(serverUA);
}

// 重新导出所有 getter 函数，方便使用
export { getUA, getPlatform, getRuntimeEnv, getContainerPlatform, getCType, getFPPlatform, getMtAppVersion } from '@/lib/getBrowserEnv';

// 重新导出类型
export type { ParsedBrowserEnv, BrowserEnvGetter } from '@/lib/getBrowserEnv';
